import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { apiErrorMessage, useAuth } from "@/AuthContext";
import { LOGOUT } from "@/constants/testIds/auth";
import AdminNav from "@/pages/AdminNav";

const WOULD_SEND = [
  ["trial_3_days", "Trial 3 days"],
  ["trial_2_days", "Trial 2 days"],
  ["trial_1_day", "Trial 1 day"],
  ["trial_ends_today", "Trial ends today"],
  ["trial_expired", "Trial expired"],
  ["winback_2_days", "Winback 2 days"],
  ["winback_5_days", "Winback 5 days"],
  ["review_request", "Review request"],
];

const SKIPPED = [
  ["paid_active", "Paid / active"],
  ["blocked", "Blocked"],
  ["admin", "Admin"],
  ["no_email", "No email"],
  ["no_matching_lifecycle_stage", "No matching lifecycle stage"],
  ["already_sent", "Already sent / duplicate"],
];

function Stat({ label, value }) {
  return (
    <article>
      <span>{label}</span>
      <strong>{value ?? "—"}</strong>
    </article>
  );
}

export default function AdminLifecycleDryRunPage() {
  const { logout } = useAuth();
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function runDry() {
    setBusy(true);
    setError("");
    try {
      const res = await api.get("/admin/lifecycle-emails/dry-run");
      setReport(res.data || null);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const would = report?.would_send || {};
  const skipped = report?.skipped || {};

  return (
    <div className="mp-shell mp-wide">
      <header className="mp-top">
        <div>
          <p className="mp-kicker">MinutePay</p>
          <h1>Lifecycle Dry Run</h1>
          <p className="mp-lead" style={{ marginBottom: 0 }}>
            Shows what the daily lifecycle job would send right now. This does not send email or write
            send records. Welcome, password reset, and weekly summary are not part of this job.
          </p>
        </div>
        <div className="mp-top-actions">
          <Button variant="outline" asChild>
            <Link to="/admin">Admin</Link>
          </Button>
          <Button data-testid={LOGOUT.button} variant="outline" onClick={logout}>
            Log out
          </Button>
        </div>
      </header>

      <AdminNav />

      <section className="mp-card mp-crm-dry">
        <h2>Daily lifecycle scan</h2>
        <p>Read-only. Zero emails. Zero lifecycle records. Users, calls, billing, and Paddle stay unchanged.</p>
        <Button onClick={runDry} disabled={busy}>
          {busy ? "Scanning…" : "Run Lifecycle Dry Run"}
        </Button>
      </section>

      {error ? <p className="mp-error">{error}</p> : null}

      {report ? (
        <>
          <p className="mp-lead">
            Dry run · emails sent: {report.emails_sent ?? 0} · records written: {report.records_written ?? 0} ·
            would send total: {report.would_send_total ?? 0} · Mongo users: {report.total_mongo_users ?? 0}
          </p>
          <h2 className="mp-crm-heading">Would send</h2>
          <section className="mp-counts mp-crm-counts">
            {WOULD_SEND.map(([key, label]) => (
              <Stat key={key} label={label} value={would[key] ?? 0} />
            ))}
          </section>
          <h2 className="mp-crm-heading">Skipped</h2>
          <section className="mp-counts mp-crm-counts">
            {SKIPPED.map(([key, label]) => (
              <Stat key={key} label={label} value={skipped[key] ?? 0} />
            ))}
          </section>
          <h2 className="mp-crm-heading">Would-send recipients</h2>
          <div className="mp-table-wrap">
            <table className="mp-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Email Type</th>
                  <th>License</th>
                  <th>Access</th>
                  <th>Calls</th>
                  <th>First Call</th>
                  <th>Trial Ends</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {(report.recipients || []).length === 0 ? (
                  <tr>
                    <td colSpan={9}>No emails would send</td>
                  </tr>
                ) : (
                  (report.recipients || []).map((row, index) => (
                    <tr key={`${row.email}-${row.email_type}-${index}`}>
                      <td>{row.full_name || "—"}</td>
                      <td>{row.email}</td>
                      <td>{row.email_type}</td>
                      <td>{row.license_status || "—"}</td>
                      <td>{row.has_access ? "Has access" : "No access"}</td>
                      <td>{row.total_calls ?? 0}</td>
                      <td>{row.first_call_at || "—"}</td>
                      <td>{row.trial_ends_at || "—"}</td>
                      <td>{row.reason}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  );
}
